import './App.css';
import { useState, useEffect, useCallback, useMemo } from 'react';
import React from 'react';

// Import CV data (единый файл с инлайн-переводами, см. cv-data/resolve.js)
import cvSource from './cv-data/cv.json';
import { resolveCv, SUPPORTED_LANGS } from './cv-data/resolve';

// Import Skills component
import Skills from './components/Skills';

// Языковые страницы вида /ja/ — реальные файлы, которые кладёт scripts/prerender.js.
const LANG_PATH_RE = new RegExp(`^/(${SUPPORTED_LANGS.join('|')})/?$`);

/**
 * Язык, зафиксированный в пути (/ja/ -> 'ja'). null — если мы на корне.
 */
function getPathLang() {
  const match = window.location.pathname.match(LANG_PATH_RE);
  return match ? match[1] : null;
}

/**
 * Hook to read and update URL search params without page reload.
 * Since this is CRA (not Next.js), we use the Web Storage API directly.
 */
function useUrlLang() {
  const getUrlLang = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('lang');
  }, []);

  const setUrlLang = useCallback((lang) => {
    if (!SUPPORTED_LANGS.includes(lang)) return;

    const url = new URL(window.location.href);
    if (getPathLang()) {
      // На путевой странице меняем путь. Оставить здесь ?lang= нельзя: получился бы
      // /ja/?lang=ru, где статическая разметка и параметр противоречат друг другу.
      url.pathname = `/${lang}/`;
      url.search = '';
    } else {
      // На корне сохраняем прежнее поведение — старые ссылки ?lang= продолжают работать.
      url.searchParams.set('lang', lang);
    }
    window.history.replaceState({}, '', url.toString());
  }, []);

  // Read initial value from URL
  const [urlLang] = useState(() => getUrlLang());
  const [pathLang] = useState(() => getPathLang());

  return { urlLang, pathLang, setUrlLang };
}

const languageLabels = {
  en: 'EN',
  ru: 'RU',
  ja: 'JA'
};

// Site configuration - should be overridden via environment variables in production
const SITE_CONFIG = {
  baseUrl: process.env.REACT_APP_SITE_URL || 'https://dswz.ru',
};

const decodePhone = (encoded) => {
  return atob(encoded);
};

/**
 * Обновляет существующий тег в <head>. Намеренно не создаёт отсутствующие:
 * инвентарь тегов задаётся в public/index.html, здесь только значения.
 */
function setTag(selector, attr, value) {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

/**
 * Manage meta tags for SEO
 */
function useSeoTags(cvData, language) {
  useEffect(() => {
    const heading = `${cvData.name} — ${cvData.title}`;
    const description = cvData.seo.description;
    // Каноническая форма адреса — всегда путевая, даже если открыли ?lang=.
    const canonical = `${SITE_CONFIG.baseUrl}/${language}/`;

    // Update document title (for social sharing when user navigates away)
    document.title = `${cvData.name} - ${cvData.title}`;
    document.documentElement.lang = language;

    setTag('meta[name="description"]', 'content', description);
    setTag('link[rel="canonical"]', 'href', canonical);
    setTag('meta[property="og:title"]', 'content', heading);
    setTag('meta[property="og:description"]', 'content', description);
    setTag('meta[property="og:url"]', 'content', canonical);
    setTag('meta[property="og:locale"]', 'content', cvData.seo.ogLocale);
    setTag('meta[name="twitter:title"]', 'content', heading);
    setTag('meta[name="twitter:description"]', 'content', description);

    // Note: JSON-LD structured data is static in index.html for better SEO
    // because crawlers need it in the initial HTML response.
    // Dynamic injection via useEffect would only work if crawler executes JS,
    // which Google does but not all search engines do.
    // Под язык его переписывает scripts/prerender.js на этапе сборки.

    // Cleanup on unmount (nothing to remove - JSON-LD is static)
    return () => {};
  }, [cvData, language]);
}

function App() {
  const [phoneVisible, setPhoneVisible] = useState(false);
  
  // Read URL path and query param on first render (before state initialization)
  const { urlLang, pathLang, setUrlLang } = useUrlLang();

  const [language, setLanguage] = useState(() => {
    // Priority: URL path > URL param > localStorage > browser detection > 'en'

    // 1. Путь /ja/ — самая явная фиксация: это отдельный пререндеренный документ,
    //    у которого мета-теги и ссылка на PDF уже собраны под этот язык.
    if (pathLang) return pathLang;

    // 2. Check URL query parameter
    if (urlLang && SUPPORTED_LANGS.includes(urlLang)) return urlLang;

    // 3. Check saved preference in localStorage
    const saved = localStorage.getItem('cv-lang');
    if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
    
    // 4. Detect from browser language
    const browserLang = navigator.language || navigator.userAgent?.locale || '';
    if (browserLang.startsWith('ru')) return 'ru';
    if (browserLang.startsWith('ja')) return 'ja';
    return 'en';
  });

  // Sync URL with language state on change
  useEffect(() => {
    setUrlLang(language);
  }, [language, setUrlLang]);

  const cvData = useMemo(() => resolveCv(cvSource, language), [language]);

  // Initialize SEO tags when CV data is loaded/changed
  useSeoTags(cvData, language);

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    setLanguage(newLang);
    localStorage.setItem('cv-lang', newLang);
  };

  return (
    // Semantic HTML: main element for primary content
    <main id="cv-content" itemScope itemType="https://schema.org/Person">
      {/* Hidden structured data via itemscope */}
      <div hidden itemScope itemType="https://schema.org/Person">
        <meta itemProp="name" content={cvData.name} />
        <meta itemProp="jobTitle" content={cvData.title} />
        <link itemProp="url" href={`${SITE_CONFIG.baseUrl}/`} />
        <link itemProp="email" href={`mailto:${cvData.contact.email}`} />
        {/* Social profiles */}
        <a itemProp="sameAs" href={`https://www.linkedin.com/in/${cvData.contact.linkedin}`}>LinkedIn</a>
        <a itemProp="sameAs" href={`https://github.com/${cvData.contact.github}`}>GitHub</a>
      </div>

      <div className="App">
        <header className="App-header">
          {/* Semantic: nav with ARIA label */}
          <nav aria-label="Language selector" className="language-selector">
            <select value={language} onChange={handleLanguageChange}>
              {Object.entries(languageLabels).map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </nav>

          {/* Heading hierarchy: h1 for main title */}
          <h1>{cvData.name}</h1>
          
          {/* Section heading: h2 for major sections */}
          <h2 className="job-title">{cvData.title}</h2>
          
          <div className="contact-info">
            <p><strong>{cvData.labels.email}:</strong> {cvData.contact.email}</p>
            <p><strong>{cvData.labels.phone}:</strong> 
              {phoneVisible ? decodePhone(cvData.contact.phone) : (
                <span className="phone-hidden" onClick={() => setPhoneVisible(true)}> {cvData.labels.clickToReveal}</span>
              )}
            </p>
            <p><strong>{cvData.labels.address}:</strong> {cvData.contact.address}</p>
          </div>

          {/* PDF собирается на этапе сборки (scripts/prerender.js) — обычная ссылка на файл */}
          <p className="pdf-download">
            <a href={`/cv-${language}.pdf`} download={`cv-${language}.pdf`}>
              {cvData.labels.downloadPdf}
            </a>
          </p>
          
          {/* Availability section - engagement terms, first thing a recruiter looks for */}
          {cvData.availability && cvData.availability.length > 0 && (
            <section className="section" id="availability" aria-labelledby="availability-heading">
              <h3 id="availability-heading">{cvData.labels.availability}</h3>
              <dl className="availability-list">
                {cvData.availability.map((item, index) => (
                  <React.Fragment key={index}>
                    <dt>{item.label}</dt>
                    <dd>{item.value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </section>
          )}

          {/* About section - semantic article with aria-labelledby */}
          <section className="section" id="about" aria-labelledby="about-heading">
            <h3 id="about-heading">{cvData.labels.about}</h3>
            <p>{cvData.about}</p>
          </section>
          
          {/* Experience section - semantic article with schema.org WorkExperience */}
          <article className="section" id="experience" aria-labelledby="experience-heading">
            <h3 id="experience-heading">{cvData.labels.experience}</h3>
            {cvData.experience.map((exp, index) => (
              <div key={index} className="experience-item" itemScope itemType="https://schema.org/WorkExperience">
                <h4>{exp.company}</h4>
                {/* Semantic: time element with datetime for machine-readable dates */}
                <p><strong>{exp.position}</strong> - <time dateTime={exp.duration.replace(/\s/g, '-')}>{exp.duration}</time></p>
                <p>{exp.description}</p>
                {exp.technologies && (
                  <div className="technologies" aria-label="Technologies used">
                    {exp.technologies.map((tech, i) => (
                      <span key={i} className="technology-tag">{tech}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </article>
          
          {/* Education section */}
          <section className="section" id="education" aria-labelledby="education-heading">
            <h3 id="education-heading">{cvData.labels.education}</h3>
            {cvData.education.map((edu, index) => (
              <div key={index} className="education-item">
                <h4>{edu.institution}</h4>
                {/* Semantic: time element for machine-readable dates */}
                <p><strong>{edu.degree}</strong> - <time dateTime={`${edu.duration}-01-01`}>{edu.duration}</time></p>
                {edu.description && <p>{edu.description}</p>}
              </div>
            ))}
          </section>
          
          {/* Skills section with proficiency visualization */}
          <Skills skills={cvData.skills} title={cvData.labels.skills} />
          
          {/* Languages section */}
          {cvData.languages && cvData.languages.length > 0 && (
            <section className="section" id="languages" aria-labelledby="languages-heading">
              <h3 id="languages-heading">{cvData.labels.languages}</h3>
              <div className="languages-list" role="list" aria-label={cvData.labels.languages}>
                {cvData.languages.map((lang, index) => (
                  <span key={index} className="language-tag" role="listitem">
                    {lang.name}: {lang.level}
                  </span>
                ))}
              </div>
            </section>
          )}
          
          {/* Hobbies section */}
          {cvData.hobbies && cvData.hobbies.length > 0 && (
            <section className="section" id="hobbies" aria-labelledby="hobbies-heading">
              <h3 id="hobbies-heading">{cvData.labels.hobbies}</h3>
              <div className="hobbies-list" role="list" aria-label={cvData.labels.hobbies}>
                {cvData.hobbies.map((hobby, index) => (
                  <span key={index} className="hobby-tag" role="listitem">{hobby}</span>
                ))}
              </div>
            </section>
          )}
          
          {/* Volunteer section */}
          {cvData.volunteer && (
            <section className="section" id="volunteer" aria-labelledby="volunteer-heading">
              <h3 id="volunteer-heading">{cvData.labels.volunteer}</h3>
              <p>{cvData.volunteer}</p>
            </section>
          )}

          {/* Footer with semantic year and copyright */}
          <footer className="App-footer" style={{marginTop: '2rem'}}>
            <time dateTime={new Date().getFullYear().toString()}>
              © {new Date().getFullYear()} {cvData.name}. 
            </time>
          </footer>
        </header>
      </div>
    </main>
  );
}

export default App;
